import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Heart } from "lucide-react";
import CandleInteraction from "./CandleInteraction";

const photos = [
  {
    src: "https://customer-assets.emergentagent.com/job_swipe-to-love/artifacts/errpfjhg_09F8E996-B539-4E83-878B-7703A8452365.JPG",
    caption: "Draped in red — my queen of every sunset",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_swipe-to-love/artifacts/ia64gx8s_6c7e5c8d-78fa-4a8b-b820-f1ac6f24bf24.JPG",
    caption: "Above the clouds, still the most stunning view",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_swipe-to-love/artifacts/5ewnyyo4_01c0d9a1-6533-46e9-bbae-e1c1b2d3ef28.JPG",
    caption: "Golden hour found its muse in you",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_swipe-to-love/artifacts/bxl3ndhd_7886f943-334b-405a-b51a-77fe3cbf5ab1.JPG",
    caption: "Royalty in a quiet moment — my forever favourite view",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_swipe-to-love/artifacts/ho45245b_895d6d82-5103-4252-a636-3df131e0fea5.JPG",
    caption: "Even the Dubai lights look up to you",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_swipe-to-love/artifacts/9ls86tg2_76AD0AB0-DFD6-405E-8AC2-D8676D00963D.JPG",
    caption: "Soft glances, softer heart — pehla nasha, every time",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_swipe-to-love/artifacts/fc4n65mk_021d2916-d1ff-4803-a61e-3788518a1fb1.JPG",
    caption: "The crown finally found a queen worthy of it",
  },
];

export default function BirthdayCard() {
  const [idx, setIdx] = useState(0);

  const next = () => setIdx((i) => (i + 1) % photos.length);
  const prev = () => setIdx((i) => (i - 1 + photos.length) % photos.length);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.9, delay: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
      className="relative w-[min(360px,88vw)] rounded-[28px] overflow-hidden grain"
      style={{
        background:
          "linear-gradient(160deg, rgba(248,245,240,0.94) 0%, rgba(232,220,196,0.92) 100%)",
        border: "1px solid rgba(212, 175, 55, 0.55)",
        boxShadow:
          "0 30px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.4) inset, 0 0 40px rgba(212,175,55,0.25)",
        backdropFilter: "blur(20px)",
      }}
      data-testid="birthday-card"
    >
      {/* Gold corner ornaments */}
      <CornerOrnament className="top-3 left-3" />
      <CornerOrnament className="top-3 right-3 rotate-90" />
      <CornerOrnament className="bottom-3 left-3 -rotate-90" />
      <CornerOrnament className="bottom-3 right-3 rotate-180" />

      <div className="px-7 pt-8 pb-6 text-center">
        {/* Tiny header */}
        <div className="text-[10px] tracking-[0.5em] uppercase text-[#D71920]/80 font-body">
          · Cabin 1A ·
        </div>

        {/* Main heading */}
        <h1 className="font-display text-5xl mt-3 leading-[1.05] text-[#1A110D] font-light">
          Happy
          <br />
          <span className="gold-shimmer italic font-medium">Birthday</span>
        </h1>
        <div className="font-display text-3xl mt-1 text-[#D71920] italic">
          Farida
        </div>

        {/* Divider */}
        <div className="flex items-center justify-center gap-2 my-5">
          <div className="h-[1px] w-12 bg-[#D4AF37]/70" />
          <Heart className="w-3 h-3 text-[#D71920]" fill="#D71920" />
          <div className="h-[1px] w-12 bg-[#D4AF37]/70" />
        </div>

        {/* Message */}
        <p className="font-display text-[17px] leading-relaxed text-[#2A1B14] italic px-2">
          May you get all the success this year,
          <br />
          and every wish you whisper come true.
        </p>

        {/* Photo carousel */}
        <div
          className="mt-6 relative rounded-2xl overflow-hidden border border-[#D4AF37]/40 shadow-[0_10px_30px_rgba(0,0,0,0.25)]"
          style={{ aspectRatio: "4/5" }}
          data-testid="photo-carousel"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.6 }}
              className="absolute inset-0"
            >
              <img
                src={photos[idx].src}
                alt={`Farida ${idx + 1}`}
                className="w-full h-full object-cover"
                draggable={false}
              />
              <div
                className="absolute inset-x-0 bottom-0 px-3 py-3 text-left"
                style={{
                  background:
                    "linear-gradient(0deg, rgba(26,17,13,0.85) 0%, transparent 100%)",
                }}
              >
                <div className="font-display italic text-[#F8F5F0] text-sm">
                  {photos[idx].caption}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Carousel buttons */}
          <button
            onClick={prev}
            data-testid="carousel-prev"
            aria-label="Previous photo"
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-[#1A110D]/45 backdrop-blur-md flex items-center justify-center text-[#F8F5F0] border border-[#D4AF37]/40 hover:bg-[#D4AF37]/30 transition"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={next}
            data-testid="carousel-next"
            aria-label="Next photo"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-[#1A110D]/45 backdrop-blur-md flex items-center justify-center text-[#F8F5F0] border border-[#D4AF37]/40 hover:bg-[#D4AF37]/30 transition"
          >
            <ChevronRight className="w-4 h-4" />
          </button>

          {/* Dots */}
          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
            {photos.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                data-testid={`carousel-dot-${i}`}
                aria-label={`Go to photo ${i + 1}`}
                className="transition-all"
                style={{
                  width: i === idx ? 18 : 6,
                  height: 4,
                  borderRadius: 2,
                  background:
                    i === idx ? "#D4AF37" : "rgba(248,245,240,0.5)",
                }}
              />
            ))}
          </div>
        </div>

        {/* Candle */}
        <div className="mt-7">
          <CandleInteraction />
        </div>

        {/* Signature line */}
        <div className="mt-7 text-[10px] tracking-[0.45em] uppercase text-[#3E2723]/70 font-body">
          With all my love
        </div>
      </div>
    </motion.div>
  );
}

function CornerOrnament({ className = "" }) {
  return (
    <svg
      className={`absolute w-7 h-7 ${className}`}
      viewBox="0 0 32 32"
      fill="none"
      stroke="#D4AF37"
      strokeWidth="1"
    >
      <path d="M2 14 L2 4 Q2 2 4 2 L14 2" />
      <path d="M2 8 L8 8 L8 2" opacity="0.7" />
      <circle cx="4" cy="4" r="1" fill="#D71920" stroke="none" />
    </svg>
  );
}
